/**
 * An `<img>` for an endpoint that requires the bearer token.
 *
 * Access tokens live in memory/localStorage, not in a cookie, so the browser does
 * not attach them to a plain `<img src>`, the request arrives unauthenticated and
 * 401s. Everything the API serves as bytes (clinic logos, uploaded lab documents,
 * body-composition figures) therefore has to be fetched through the API client and
 * handed to the DOM as an object URL.
 *
 * Object URLs are revoked on unmount and whenever `src` changes, so a long session
 * browsing many patients does not accumulate blobs.
 */

import { useEffect, useState } from 'react';
import { api } from '../lib/api';

interface AuthedImageProps {
  /** API-relative path, e.g. `/files/<key>`. */
  src: string | null | undefined;
  alt: string;
  className?: string;
  /** Rendered while loading or when `src` is absent or fails. */
  fallback?: React.ReactNode;
}

export function AuthedImage({ src, alt, className, fallback = null }: AuthedImageProps) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!src) {
      setObjectUrl(null);
      setFailed(false);
      return;
    }

    let revoked = false;
    let created: string | null = null;
    setFailed(false);

    void (async () => {
      try {
        // Paths handed back by the API are absolute (`/api/v1/...`); the client
        // prefixes relative ones.
        const path = src.replace(/^\/api\/v1/, '');
        const response = await api.get<Response>(path, { raw: true });
        const blob = await response.blob();
        if (revoked) return;
        created = URL.createObjectURL(blob);
        setObjectUrl(created);
      } catch {
        if (!revoked) setFailed(true);
      }
    })();

    return () => {
      revoked = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [src]);

  if (!src || failed || !objectUrl) return <>{fallback}</>;
  return <img src={objectUrl} alt={alt} className={className} />;
}

/**
 * Open an authenticated file in a new tab.
 *
 * Same reason as above: a plain anchor would not carry the token. Fetches the
 * bytes, opens them as an object URL, and releases it once the tab has had a
 * chance to load.
 */
export async function openAuthedFile(path: string): Promise<void> {
  const response = await api.get<Response>(path.replace(/^\/api\/v1/, ''), { raw: true });
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  // Long enough for the new tab to fetch it; short enough not to leak.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
